import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../types';
import type { OrderDoc, PaginatedOrders } from './ordersSlice';

export interface RecentTransactionsState {
  loading: boolean;
  error: string | null;
  recent: PaginatedOrders;
  lastFetchedAt: string | null;
}

const createInitialPage = (): PaginatedOrders => ({
  docs: [],
  hasNextPage: true,
  hasPrevPage: false,
  limit: 50,
  nextPage: 1,
  page: 1,
  perPage: 50,
  totalDocs: 0,
  totalPages: 0,
});

const initialState: RecentTransactionsState = {
  loading: false,
  error: null,
  recent: createInitialPage(),
  lastFetchedAt: null,
};

const mergeOrders = (
  existing: OrderDoc[],
  incoming: OrderDoc[],
): OrderDoc[] => {
  const orderMap = new Map<string, OrderDoc>();
  [...existing, ...incoming].forEach(order => {
    const key = String(order?.bp_order_id || '');
    if (!key) return;
    const previous = orderMap.get(key);
    if (
      !previous ||
      new Date(order.bp_order_master_updated_at).getTime() >
        new Date(previous.bp_order_master_updated_at).getTime()
    ) {
      orderMap.set(key, { ...order });
    }
  });
  return Array.from(orderMap.values()).sort(
    (a, b) =>
      new Date(b.bp_order_master_updated_at).getTime() -
      new Date(a.bp_order_master_updated_at).getTime(),
  );
};

const recentTransactionsSlice = createSlice({
  name: 'backofficeRecentTransactions',
  initialState,
  reducers: {
    setRecentTransactionsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setRecentTransactionsError: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.error = action.payload;
    },
    setRecentTransactions: (state, action: PayloadAction<PaginatedOrders>) => {
      state.recent = {
        ...state.recent,
        ...action.payload,
        docs: mergeOrders([], action.payload.docs || []),
      };
      state.lastFetchedAt = new Date().toISOString();
    },
    addRecentTransactionsPage: (
      state,
      action: PayloadAction<PaginatedOrders>,
    ) => {
      state.recent = {
        ...state.recent,
        ...action.payload,
        docs: mergeOrders(state.recent.docs, action.payload.docs || []),
      };
      state.lastFetchedAt = new Date().toISOString();
    },
    clearRecentTransactions: state => {
      state.recent = createInitialPage();
      state.error = null;
      state.lastFetchedAt = null;
    },
    upsertRecentTransaction: (state, action: PayloadAction<OrderDoc>) => {
      state.recent.docs = mergeOrders(state.recent.docs, [action.payload]);
      state.lastFetchedAt = new Date().toISOString();
    },
  },
});

export const {
  setRecentTransactionsLoading,
  setRecentTransactionsError,
  setRecentTransactions,
  addRecentTransactionsPage,
  clearRecentTransactions,
  upsertRecentTransaction,
} = recentTransactionsSlice.actions;

export const selectRecentTransactionsState = (state: RootState) =>
  state.backofficeRecentTransactions;
export const selectRecentTransactionsPage = (state: RootState) =>
  state.backofficeRecentTransactions.recent;
export const selectRecentTransactionsDocs = createSelector(
  [selectRecentTransactionsPage],
  recent => recent.docs || [],
);

export default recentTransactionsSlice.reducer;
